import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Close as CloseIcon, EditOutlined as EditIcon, Add as AddIcon } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { INDIGO, SLATE } from "../../constants/Colors";
import { usePageMeta } from "../../contexts/PageMetaContext";
import useApiCall from "../../hooks/useApiCall";
import LoadingSpinner from "../../components/LoadingSpinner";
import SearchInput from "../../components/SearchInput";
import TrimTextField from "../../components/TrimTextField";
import { dateFormatter, rolesSelectorBasedOnHierarchy } from "../../utils";
import { APP_USER_ROLES } from "../../constants/Constants";

const INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "0.875rem",
    bgcolor: "#ffffff",
    "& fieldset": { borderColor: SLATE[300] },
    "&:hover fieldset": { borderColor: SLATE[400] },
    "&.Mui-focused fieldset": { borderColor: INDIGO[500] },
  },
  "& .MuiInputBase-input": { color: SLATE[900] },
};

const FIELD_LABEL_SX = {
  fontSize: "0.8125rem",
  fontWeight: 500,
  color: SLATE[700],
  mb: "6px",
  display: "block",
};

const EMPTY_FORM = { email: "", name: "", role: "client_user" };

export default function AccessControl() {
  const currentUser = useSelector((state) => state.user.value);
  const tokens = useSelector((state) => state?.tokens?.value);
  const currentApp = useSelector((state) => state?.apps?.selectedApp);
  const { makeRequest } = useApiCall();
  const { setPageMeta } = usePageMeta();

  const [appUsers, setAppUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});

  const roleOptions = rolesSelectorBasedOnHierarchy(currentUser.user_type, APP_USER_ROLES);

  useEffect(() => {
    setPageMeta({ description: "Manage who has access to this client and their roles." });
    return () => setPageMeta({});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!currentApp?.id) return;
    makeRequest(`/v1/apps/${currentApp.id}/users`, { method: "GET", authTokens: tokens })
      .then((data) => {
        if (Array.isArray(data)) {
          setAppUsers(data.map((u) => ({ ...u, role: u.app_user?.role || u.role })));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [currentApp?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredUsers = search.trim()
    ? appUsers.filter(
        (u) =>
          u.email?.toLowerCase().includes(search.toLowerCase()) ||
          u.name?.toLowerCase().includes(search.toLowerCase())
      )
    : appUsers;

  const openAddDialog = () => {
    setEditingUser(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
    setDialogOpen(true);
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setForm({ email: user.email || "", name: user.name || "", role: user.role || "client_user" });
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingUser(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
  };

  const handleSave = async () => {
    const errors = {};
    if (!form.email.trim()) errors.email = true;
    if (!form.name.trim()) errors.name = true;
    if (!form.role) errors.role = true;
    if (Object.keys(errors).length) { setFormErrors(errors); return; }

    setSaving(true);
    try {
      const payload = editingUser
        ? [{ id: editingUser.id, ...form }]
        : [{ ...form }];
      const response = await makeRequest(`/v1/apps/${currentApp.id}/users/upsert`, {
        method: "POST",
        body: { users: payload },
        authTokens: tokens,
      });
      if (Array.isArray(response)) {
        if (editingUser) {
          setAppUsers((prev) =>
            prev.map((u) => {
              const updated = response.find((r) => r.id === u.id);
              return updated ? { ...updated, role: updated.app_user?.role || updated.role } : u;
            })
          );
        } else {
          const newUser = response[0];
          if (newUser) setAppUsers((prev) => [...prev, { ...newUser, role: newUser.app_user?.role || newUser.role }]);
        }
      }
      handleDialogClose();
    } catch {
      // error already toasted by useApiCall
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner message="Loading users..." subMessage="Fetching access control list" />;

  return (
    <Box sx={{ width: "100%" }}>
      {/* Toolbar */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search by name or email..."
        />
        <Button
          variant="contained"
          startIcon={<AddIcon sx={{ fontSize: "1rem" }} />}
          onClick={openAddDialog}
          sx={{ textTransform: "none", fontSize: "0.875rem", fontWeight: 500, boxShadow: "none", "&:hover": { boxShadow: "none" } }}
        >
          Add User
        </Button>
      </Box>

      {/* Card */}
      <Box
        sx={{
          bgcolor: "#ffffff",
          border: `1px solid ${SLATE[200]}`,
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 1px 3px 0 rgba(0,0,0,0.05), 0 1px 2px -1px rgba(0,0,0,0.04)",
        }}
      >
        {/* Column headers */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 160px 160px 48px",
            px: 3,
            py: "9px",
            borderBottom: `1px solid ${SLATE[200]}`,
            bgcolor: SLATE[50],
          }}
        >
          {["Email", "Name", "Role", "Onboarded On", ""].map((col) => (
            <Typography
              key={col}
              sx={{ fontSize: "0.6875rem", fontWeight: 600, color: SLATE[500], textTransform: "uppercase", letterSpacing: "0.055em" }}
            >
              {col}
            </Typography>
          ))}
        </Box>

        {filteredUsers.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 10 }}>
            <Typography sx={{ fontSize: "2rem", mb: 1 }}>🔒</Typography>
            <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: SLATE[900], mb: "6px" }}>
              {search ? "No users match your search" : "No users yet"}
            </Typography>
            <Typography sx={{ fontSize: "0.875rem", color: SLATE[500] }}>
              {search ? "Try a different name or email." : "Add users to grant them access to this client."}
            </Typography>
          </Box>
        ) : (
          filteredUsers.map((user, idx) => (
            <Box
              key={user.id || idx}
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 160px 160px 48px",
                px: 3,
                py: "14px",
                alignItems: "center",
                borderBottom: idx < filteredUsers.length - 1 ? `1px solid ${SLATE[100]}` : "none",
                "&:hover": { bgcolor: SLATE[50] },
                transition: "background-color 0.12s",
              }}
            >
              <Typography sx={{ fontSize: "0.875rem", color: SLATE[700], overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", pr: 2 }}>
                {user.email || "—"}
              </Typography>
              <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: SLATE[900], overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", pr: 2 }}>
                {user.name || "—"}
              </Typography>
              <Typography sx={{ fontSize: "0.8125rem", color: SLATE[600] }}>
                {APP_USER_ROLES.find((r) => r.value === user.role)?.title || user.role || "—"}
              </Typography>
              <Typography sx={{ fontSize: "0.8125rem", color: SLATE[500] }}>
                {user.created_at && user.created_at !== "-" ? dateFormatter(user.created_at, "MM/DD/YYYY") : "—"}
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Tooltip title="Edit" placement="top">
                  <IconButton
                    size="small"
                    onClick={() => openEditDialog(user)}
                    sx={{ color: SLATE[400], "&:hover": { color: INDIGO[600], bgcolor: INDIGO[50] } }}
                  >
                    <EditIcon sx={{ fontSize: "1rem" }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          ))
        )}
      </Box>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
          <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: SLATE[900] }}>
            {editingUser ? "Edit User" : "Add User"}
          </Typography>
          <IconButton size="small" onClick={handleDialogClose} sx={{ color: SLATE[400] }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: "8px !important", display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>
              Email Address <Box component="span" sx={{ color: "#ef4444" }}>*</Box>
            </Typography>
            <TrimTextField
              fullWidth size="small"
              placeholder="user@example.com"
              value={form.email}
              disabled={!!editingUser}
              onChange={(e) => { setForm((p) => ({ ...p, email: e.target.value })); setFormErrors((p) => ({ ...p, email: false })); }}
              error={formErrors.email}
              helperText={formErrors.email ? "Email is required" : ""}
              sx={{ ...INPUT_SX, ...(editingUser && { "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: SLATE[500] } }) }}
            />
          </Box>

          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>
              Full Name <Box component="span" sx={{ color: "#ef4444" }}>*</Box>
            </Typography>
            <TrimTextField
              fullWidth size="small"
              placeholder="e.g. Jane Smith"
              value={form.name}
              onChange={(e) => { setForm((p) => ({ ...p, name: e.target.value })); setFormErrors((p) => ({ ...p, name: false })); }}
              error={formErrors.name}
              helperText={formErrors.name ? "Name is required" : ""}
              sx={INPUT_SX}
            />
          </Box>

          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>
              Role <Box component="span" sx={{ color: "#ef4444" }}>*</Box>
            </Typography>
            <Autocomplete
              size="small"
              options={roleOptions}
              value={roleOptions.find((r) => r.value === form.role) || null}
              getOptionLabel={(o) => o.title}
              disableClearable
              componentsProps={{ popper: { sx: { zIndex: 21474836 } } }}
              onChange={(_, val) => { setForm((p) => ({ ...p, role: val?.value || "" })); setFormErrors((p) => ({ ...p, role: false })); }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={formErrors.role}
                  helperText={formErrors.role ? "Role is required" : ""}
                  sx={INPUT_SX}
                />
              )}
            />
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, pt: 1 }}>
            <Button
              onClick={handleDialogClose}
              variant="outlined"
              size="small"
              sx={{ borderColor: SLATE[300], color: SLATE[700], textTransform: "none", "&:hover": { borderColor: SLATE[400], bgcolor: SLATE[50] } }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              variant="contained"
              size="small"
              sx={{ textTransform: "none", fontSize: "0.875rem", fontWeight: 500, boxShadow: "none", "&:hover": { boxShadow: "none" } }}
            >
              {saving && <CircularProgress size={14} sx={{ color: "#fff", mr: 1 }} />}
              {editingUser ? "Save Changes" : "Add User"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
